import React, { ReactNode } from 'react';
import { usePermissionsContext } from '../context/permissions-context';

interface PermissionGuardProps {
  permission: string | { slug: string; entryPoint?: string; groupName?: string; action?: string };
  children: ReactNode;
  showMessage?: boolean;
  fallback?: ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  children,
  showMessage = false,
  fallback = null
}) => {
  const { hasPermission, showPermissionDenied } = usePermissionsContext();

  const hasAccess = typeof permission === 'string'
    ? hasPermission(permission)
    : hasPermission(permission.slug, permission.entryPoint, permission.groupName, permission.action);

  React.useEffect(() => {
    if (showMessage && !hasAccess) {
      const permissionSlug = typeof permission === 'string' ? permission : permission.slug;
      showPermissionDenied(permissionSlug);
    }
  }, [hasAccess, showMessage, permission, showPermissionDenied]);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

interface AnyPermissionGuardProps {
  permissions: (string | { slug: string; entryPoint?: string; groupName?: string; action?: string })[];
  children: ReactNode;
  showMessage?: boolean;
  fallback?: ReactNode;
}

export const AnyPermissionGuard: React.FC<AnyPermissionGuardProps> = ({
  permissions,
  children,
  showMessage = false,
  fallback = null
}) => {
  const { hasAnyPermission, showPermissionDenied } = usePermissionsContext();

  const permissionSlugs = permissions.map(p => typeof p === 'string' ? p : p.slug);
  const hasAccess = hasAnyPermission(permissionSlugs);

  React.useEffect(() => {
    if (showMessage && !hasAccess) {
      showPermissionDenied(permissionSlugs.join(' OR '));
    }
  }, [hasAccess, showMessage, permissionSlugs, showPermissionDenied]);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

interface AllPermissionsGuardProps {
  permissions: (string | { slug: string; entryPoint?: string; groupName?: string; action?: string })[];
  children: ReactNode;
  showMessage?: boolean;
  fallback?: ReactNode;
}

export const AllPermissionsGuard: React.FC<AllPermissionsGuardProps> = ({
  permissions,
  children,
  showMessage = false,
  fallback = null
}) => {
  const { hasAllPermissions, showPermissionDenied } = usePermissionsContext();

  const permissionSlugs = permissions.map(p => typeof p === 'string' ? p : p.slug);
  const hasAccess = hasAllPermissions(permissionSlugs);

  React.useEffect(() => {
    if (showMessage && !hasAccess) {
      showPermissionDenied(permissionSlugs.join(' AND '));
    }
  }, [hasAccess, showMessage, permissionSlugs, showPermissionDenied]);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Component for checking permission by slug dynamically
export const PermissionGuardBySlug: React.FC<{
  permissionSlug: string;
  children: ReactNode;
  showMessage?: boolean;
  fallback?: ReactNode;
}> = ({
  permissionSlug,
  children,
  showMessage = false,
  fallback = null
}) => {
  const { permissions, hasPermission, showPermissionDenied } = usePermissionsContext();

  // Find the right slug
  const permission = permissions.find(p => p.slug === permissionSlug);

  const hasAccess = permission ? hasPermission(
    permission.slug,
    permission.entry_point,
    permission.group_name,
    permission.action
  ) : false;

  React.useEffect(() => {
    if (showMessage && !hasAccess) {
      showPermissionDenied(permissionSlug);
    }
  }, [hasAccess, showMessage, permissionSlug, showPermissionDenied]);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Component for checking multiple permissions by slugs dynamically
export const AnyPermissionGuardBySlugs: React.FC<{
  permissionSlugs: string[];
  children: ReactNode;
  showMessage?: boolean;
  fallback?: ReactNode;
}> = ({
  permissionSlugs,
  children,
  showMessage = false,
  fallback = null
}) => {
  const { hasAnyPermission, showPermissionDenied } = usePermissionsContext();

  const hasAccess = hasAnyPermission(permissionSlugs);

  React.useEffect(() => {
    if (showMessage && !hasAccess) {
      showPermissionDenied(permissionSlugs.join(' OR '));
    }
  }, [hasAccess, showMessage, permissionSlugs, showPermissionDenied]);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};